// Agent → operator interactions for the active conversation. Surfaces the
// oldest pending one so the UI can prompt the operator.
//
// Delivery is defense-in-depth: an initial HTTP fetch on mount, a `#interaction#
// {convId}` websocket subscription for realtime, AND a slow poll backstop. The
// fetch + poll matter because the WS INSERT can be missed (subscription
// activates after the agent wrote the row) or the WS push path can be flaky —
// without them a pending prompt could never surface.
//
// Answering a credential interaction is two POSTs: (1) stash the operator's
// inputs as an ephemeral broker secret, which returns a secret_ref; (2) record
// that secret_ref as the interaction's response. The real values go to SSM via
// the broker, never into the (streamed, queryable) interaction row.
import { useCallback, useEffect, useState } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { type WebSocketMessage } from '@/hooks/useWebSocket';
import { useAuth } from '@/state/auth';
import type { Interaction } from '@/types';

const POLL_INTERVAL_MS = 15_000;

function tenantAccount(tenant: string): string {
  const colon = tenant.indexOf(':');
  return colon === -1 ? tenant : tenant.slice(0, colon);
}

function mergeInteraction(
  list: Interaction[],
  updated: Interaction
): Interaction[] {
  const idx = list.findIndex(i => i.key === updated.key);
  if (idx >= 0) {
    return [...list.slice(0, idx), updated, ...list.slice(idx + 1)];
  }
  return [...list, updated];
}

export function useInteractions(conversationId?: string) {
  const axios = useAxios();
  const { isImpersonating, friend, me } = useAuth();
  const currentUser = isImpersonating ? friend : me;
  const [interactions, setInteractions] = useState<Interaction[]>([]);

  const handleEvent = useCallback(
    (ws: WebSocketMessage) => {
      if (!currentUser) return;
      if (ws.tenant && tenantAccount(ws.tenant) !== currentUser) return;
      if (ws.eventName === 'REMOVE') {
        setInteractions(prev => prev.filter(i => i.key !== ws.key));
        return;
      }
      const rows = (ws.item?.interactions ?? []) as Interaction[];
      if (rows.length === 0) return;
      setInteractions(prev => mergeInteraction(prev, rows[0]));
    },
    [currentUser]
  );

  useRealtimeSubscription({
    patterns: conversationId
      ? [{ pattern: `#interaction#${conversationId}`, matchType: 'prefix' }]
      : [],
    enabled: !!conversationId,
    onUpdate: handleEvent,
  });

  // Fetch pending interactions on mount + poll as a backstop for missed/flaky
  // WS pushes. Each fetch merges by key, so it dovetails with realtime updates
  // and the optimistic answer below without clobbering them.
  const fetchPending = useCallback(async () => {
    if (!conversationId) return;
    try {
      // convId lets /my resolve the conversation's partition server-side
      // (per-user vs tenant for hunt/Public) instead of guessing ?user.
      const resp = await axios.get('/my', {
        params: {
          key: `#interaction#${conversationId}`,
          convId: conversationId,
        },
      });
      const rows = (resp.data?.interactions ?? []) as Interaction[];
      if (rows.length > 0) {
        setInteractions(prev => rows.reduce(mergeInteraction, prev));
      }
    } catch {
      // Best-effort backstop; the WS subscription is the primary path.
    }
  }, [axios, conversationId]);

  useEffect(() => {
    if (!conversationId) return;
    // setState happens after the await inside fetchPending (a later microtask),
    // not synchronously in this effect — same mount-fetch shape as useChatWorker.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch backstop, no synchronous setState
    void fetchPending();
    const id = setInterval(() => void fetchPending(), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [conversationId, fetchPending]);

  // Oldest unanswered interaction for this conversation — what to prompt next.
  const pending = interactions
    .filter(i => i.conversationId === conversationId && i.status === 'pending')
    .sort((a, b) => (a.timestamp ?? '').localeCompare(b.timestamp ?? ''))[0];

  const submitCredential = useCallback(
    async (requestId: string, inputs: Record<string, string>) => {
      if (!conversationId) {
        throw new Error('cannot submit an interaction without a conversation');
      }
      // Ephemeral is a generic KV store — the params ARE the key/value payload.
      const broker = await axios.post('/broker', {
        operation: 'add',
        type: 'ephemeral',
        category: 'env-integration',
        parameters: inputs,
      });
      const secretRef = broker.data?.credentialValue?.credential_id;
      if (!secretRef) {
        throw new Error('broker did not return a credential id');
      }
      await axios.post('/planner/interaction', {
        conversationId,
        requestId,
        response: secretRef,
      });
      // Optimistic: mark answered so the prompt closes before the WS echo.
      setInteractions(prev =>
        prev.map(i =>
          i.requestId === requestId ? { ...i, status: 'answered' } : i
        )
      );
    },
    [axios, conversationId]
  );

  return { pending, submitCredential };
}
